import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { normalizeGovernance } from './llmGatewayConstants';
import { llmGatewayApi } from './llmGatewayApi';
import { logLlmGateway } from './llmGatewayLogger';
import type { GatewayFormState, GatewayStatus, GovernanceSettings, LlmGateway, RegisteredLlm } from './llmGatewayTypes';

export function gatewayToForm(gateway: LlmGateway): GatewayFormState {
  return {
    name: gateway.name,
    llmType: gateway.llmType,
    modelId: gateway.modelId,
    apiTypes: [...gateway.apiTypes],
    customEndpoint: gateway.customEndpoint,
    authMethod: gateway.authMethod,
    governance: normalizeGovernance(gateway.governance),
  };
}

type LlmGatewayContextValue = {
  ready: boolean;
  loading: boolean;
  loadError: string | null;
  modelsLoading: boolean;
  modelsError: string | null;
  models: RegisteredLlm[];
  gateways: LlmGateway[];
  reload: () => Promise<void>;
  reloadModels: () => Promise<void>;
  getModel: (id: string) => RegisteredLlm | undefined;
  getGateway: (id: string) => LlmGateway | undefined;
  saveGateway: (form: GatewayFormState, options: { id?: string; status: GatewayStatus }) => Promise<LlmGateway>;
  updateGovernance: (id: string, governance: GovernanceSettings) => Promise<LlmGateway | null>;
  setStatus: (id: string, status: GatewayStatus) => Promise<void>;
  deleteGateway: (id: string) => Promise<void>;
  generateApiKey: (id: string) => Promise<string | null>;
  testConnection: (id: string) => Promise<{ ok: boolean; message: string }>;
};

const LlmGatewayContext = createContext<LlmGatewayContextValue | null>(null);

export function LlmGatewayProvider({ children }: { children: React.ReactNode }) {
  const [models, setModels] = useState<RegisteredLlm[]>([]);
  const [gateways, setGateways] = useState<LlmGateway[]>([]);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState<string | null>(null);

  const hydrateModels = useCallback(async () => {
    setModelsLoading(true);
    setModelsError(null);
    try {
      const nextModels = await llmGatewayApi.listModels();
      setModels(nextModels);
      setModelsError(null);
      logLlmGateway('app', 'store_models_hydrate', { models: nextModels.length, source: 'api' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load registered models.';
      setModelsError(message);
      logLlmGateway('warning', 'store_models_hydrate_failed', { message });
    } finally {
      setModelsLoading(false);
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setModelsError(null);
    setModelsLoading(true);
    const modelsPromise = hydrateModels();
    try {
      const nextGateways = await llmGatewayApi.listGateways();
      setGateways(nextGateways);
      setReady(true);
      logLlmGateway('app', 'store_hydrate', { gateways: nextGateways.length, source: 'api' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load LLM Gateway.';
      setLoadError(message);
      logLlmGateway('error', 'store_hydrate_failed', { message });
    } finally {
      setLoading(false);
    }
    await modelsPromise;
  }, [hydrateModels]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const replaceGateway = useCallback((saved: LlmGateway) => {
    setGateways((current) => {
      const exists = current.some((item) => item.id === saved.id);
      return exists ? current.map((item) => (item.id === saved.id ? saved : item)) : [saved, ...current];
    });
  }, []);

  const getModel = useCallback((id: string) => models.find((item) => item.id === id), [models]);
  const getGateway = useCallback((id: string) => gateways.find((item) => item.id === id), [gateways]);

  const saveGateway = useCallback(
    async (form: GatewayFormState, options: { id?: string; status: GatewayStatus }) => {
      const saved = await llmGatewayApi.saveGateway(form, options);
      replaceGateway(saved);
      return saved;
    },
    [replaceGateway]
  );

  const updateGovernance = useCallback(
    async (id: string, governance: GovernanceSettings) => {
      const saved = await llmGatewayApi.updateGovernance(id, governance);
      replaceGateway(saved);
      return saved;
    },
    [replaceGateway]
  );

  const setStatus = useCallback(
    async (id: string, status: GatewayStatus) => {
      const saved = await llmGatewayApi.updateStatus(id, status);
      replaceGateway(saved);
    },
    [replaceGateway]
  );

  const deleteGateway = useCallback(async (id: string) => {
    await llmGatewayApi.deleteGateway(id);
    setGateways((current) => current.filter((item) => item.id !== id));
  }, []);

  const generateApiKey = useCallback(
    async (id: string) => {
      const key = await llmGatewayApi.generateApiKey(id);
      setGateways((current) =>
        current.map((item) => (item.id === id ? { ...item, hasApiKey: true, apiKeyLast4: key.slice(-4) } : item))
      );
      return key;
    },
    []
  );

  const testConnection = useCallback((id: string) => llmGatewayApi.testConnection(id), []);

  const value = useMemo<LlmGatewayContextValue>(
    () => ({
      ready,
      loading,
      loadError,
      modelsLoading,
      modelsError,
      models,
      gateways,
      reload,
      reloadModels: hydrateModels,
      getModel,
      getGateway,
      saveGateway,
      updateGovernance,
      setStatus,
      deleteGateway,
      generateApiKey,
      testConnection,
    }),
    [
      deleteGateway,
      generateApiKey,
      gateways,
      getGateway,
      getModel,
      hydrateModels,
      loadError,
      loading,
      models,
      modelsError,
      modelsLoading,
      ready,
      reload,
      saveGateway,
      setStatus,
      testConnection,
      updateGovernance,
    ]
  );

  return <LlmGatewayContext.Provider value={value}>{children}</LlmGatewayContext.Provider>;
}

export function useLlmGateway(): LlmGatewayContextValue {
  const context = useContext(LlmGatewayContext);
  if (!context) {
    throw new Error('useLlmGateway must be used within LlmGatewayProvider');
  }
  return context;
}
